import React, {useEffect, useRef, useState} from 'react';
import AboutSubSection0201 from "./sub/AboutSubSection0201";
import AboutSubSection0202 from "./sub/AboutSubSection0202";
import AboutSubSection0203 from "./sub/AboutSubSection0203";
import AboutSubSection0204 from "./sub/AboutSubSection0204";
import AboutSubSection0205 from "./sub/AboutSubSection0205";
import AboutSubSection0206 from "./sub/AboutSubSection0206";
import AboutSubSection0207 from "./sub/AboutSubSection0207";
import arrowLeft from "../../assets/images/about/section_02_mobile_arrow_down.png";


// 섹션 데이터 타입 정의
interface SectionData {
  id: string;
  title: string;
  content: React.ReactNode;
}

function AboutSection2() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const sectionRef = useRef<HTMLElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const headerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [lineBreaks, setLineBreaks] = useState<number[]>([]);

  // 섹션 데이터 정의
  const sections: SectionData[] = [
    {
      id: 'section1',
      title: 'PONPISH, 컬러렌즈 플랫폼',
      content: (
        <AboutSubSection0201 />
      )
    },
    {
      id: 'section2',
      title: 'Offline Store',
      content: (
        <AboutSubSection0202 />
      )
    },
    {
      id: 'section3',
      title: 'Influencer Marketing',
      content: (
        <AboutSubSection0203 />
      )
    },
    {
      id: 'section4',
      title: 'Distribution Channels',
      content: (
        <AboutSubSection0204 />
      )
    },
    {
      id: 'section5',
      title: 'Exclusive Brand License',
      content: (
        <AboutSubSection0205 />
      )
    },
    {
      id: 'section6',
      title: 'Japan Distribution & Marketing',
      content: (
        <AboutSubSection0206 />
      )
    },
    {
      id: 'section7',
      title: 'Global Trade',
      content: (
        <AboutSubSection0207 />
      )
    }
  ];

  // 줄바꿈 감지 함수
  const detectLineBreaks = () => {
    if (itemRefs.current.length === 0) return;

    const breaks: number[] = [];
    let prevTop = itemRefs.current[0]?.getBoundingClientRect().top || 0;
    
    itemRefs.current.forEach((item, index) => {
      if (!item) return;
      
      const rect = item.getBoundingClientRect();
      if (rect.top > prevTop + 5) { // 5px 이상 차이가 나면 줄바꿈으로 간주
        breaks.push(index);
        prevTop = rect.top;
      }
    });
    
    setLineBreaks([0, ...breaks, sections.length]);
  };

  // 스크롤 위치에 따라 활성 섹션 업데이트
  const updateActiveSection = () => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const scrollPosition = window.scrollY + headerHeight + 160; // 헤더 높이 + 여유 공간

    // 각 섹션의 위치 확인
    for (const id of Object.keys(sectionRefs.current)) {
      const section = sectionRefs.current[id];
      if (!section) continue;

      const sectionTop = section.offsetTop;
      const sectionBottom = sectionTop + section.offsetHeight;

      // 현재 스크롤 위치가 섹션 내에 있는지 확인
      if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
        setActiveSection(id);
        break;
      }
    }
  };

  // 특정 섹션으로 스크롤
  const scrollToSection = (id: string) => {
    const section = sectionRefs.current[id];
    if (!section) return;

    const headerHeight = headerRef.current?.offsetHeight || 0;
    const sectionTop = section.offsetTop;
    
    window.scrollTo({
      top: sectionTop - headerHeight,
      behavior: 'smooth'
    });
    
    setActiveSection(id);
  };

  // 아이템이 줄의 첫 번째 또는 마지막인지 확인
  const isFirstOrLastInLine = (index: number): { isFirst: boolean, isLast: boolean } => {
    if (lineBreaks.length < 2) return { isFirst: index === 0, isLast: index === sections.length - 1 };
    
    for (let i = 0; i < lineBreaks.length - 1; i++) {
      if (index === lineBreaks[i]) return { isFirst: true, isLast: false };
      if (index === lineBreaks[i + 1] - 1) return { isFirst: false, isLast: true };
      if (index > lineBreaks[i] && index < lineBreaks[i + 1] - 1) return { isFirst: false, isLast: false };
    }
    
    return { isFirst: false, isLast: false };
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // 섹션이 화면에 보일 때만 애니메이션 적용
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          // 섹션이 화면에서 벗어났을 때
          setIsVisible(false);
        }
      },
      {
        threshold: 0.2, // 섹션이 20% 이상 보일 때 트리거
        rootMargin: '-50px' // 뷰포트 경계에서 50px 안쪽에서 트리거
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // 스크롤 이벤트 리스너 등록
    window.addEventListener('scroll', updateActiveSection);
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      window.removeEventListener('scroll', updateActiveSection);
    };
  }, []);

  // 줄바꿈 감지를 위한 useEffect
  useEffect(() => {
    // 초기 렌더링 후 줄바꿈 감지
    detectLineBreaks();
    
    // 창 크기 변경 시 줄바꿈 다시 감지
    const handleResize = () => {
      detectLineBreaks();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // itemRefs가 업데이트된 후 줄바꿈 감지
  useEffect(() => {
    if (itemRefs.current.length === sections.length) {
      detectLineBreaks();
    }
  }, [itemRefs.current.length]);

  return (
    <section
      id="business"
      ref={sectionRef}
      className="min-h-[740px] w-full flex flex-col"
    >
      {/*<div className="bg-black mb-[20px] md:mb-[40px] mt-[48px] md:mt-[60px] lg:hidden">*/}
      {/*  <p className="text-white text-26 md:text-26 font-semibold text-center">Business</p>*/}
      {/*</div>*/}

      {/* Sticky 헤더 네비게이션 */}
      <div
        ref={headerRef}
        className="sticky top-[40px] md:top-[60px] lg:top-[80px] bg-black shadow-md z-10 w-full"
      >
        <div className="container mx-auto">
          {/* 큰 화면에서는 버튼 형태로 표시 */}
          <div className="hidden justify-center lg:flex overflow-x-auto scrollbar-hide">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`px-6 py-4 text-lg transition-all ${
                  activeSection === section.id
                    ? 'text-white font-bold underline'
                    : 'text-white font-light hover:underline'
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>

          {/* 작은 화면에서는 | 구분자로 나뉜 텍스트 형태로 표시 */}
          <div className="lg:hidden py-4 flex flex-wrap justify-center items-center">
            {sections.map((section, index) => {
              // ref 콜백 함수로 각 항목에 ref 할당
              const setItemRef = (el: HTMLSpanElement | null) => {
                itemRefs.current[index] = el;
              };

              const {isFirst, isLast} = isFirstOrLastInLine(index);

              return (
                <React.Fragment key={section.id}>
                  <span
                    ref={setItemRef}
                    onClick={() => scrollToSection(section.id)}
                    className={`inline-flex items-center text-lg px-2 py-1 cursor-pointer transition-all ${
                      activeSection === section.id
                        ? 'text-white font-bold underline'
                        : 'text-white font-light'
                    }`}
                  >
                    {section.title}
                  </span>
                  {!isLast && index < sections.length - 1 && (
                    <span className="text-white inline-flex items-center px-1">|</span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <div className="lg:hidden w-full flex pt-[80px] md:pt-[120px] bg-[#F7F7F7] justify-center">
        <img src={arrowLeft} alt="Previous" className="w-[48px] md:w-[80px] lg:w-[125px] h-auto"/>
      </div>

      {/* 섹션 콘텐츠 */}
      <div className="w-full mx-auto bg-[#F7F7F7]">
        {sections.map((section) => (
          <div
            key={section.id}
            id={section.id}
            ref={(el) => (sectionRefs.current[section.id] = el)}
          >
            {section.content}
          </div>
        ))}
      </div>
    </section>
  );
}

export default AboutSection2;